import { yupResolver } from "@hookform/resolvers/yup";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { IdNameList } from "../../api/services/common/models";
import {
  useMdmAddActivityMutation,
  useMdmGetViewActivityDetailById,
  useMdmSearchActivityMutation,
  useMdmUpdateActivityMutation,
} from "../../api/services/mdmActivity/mdmActivityApis";
import { SearchMdmActivityParams } from "../../api/services/mdmActivity/models";
import { ReactComponent as WarningIcon } from "../../assets/images/warning-icon.svg";
import { InformationBadge } from "../../common/informationBadge/informationBadge";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
  MuiModal,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../components";
import {
  BE_ERROR_CODES,
  EQUAL_TO_FILTER_SITE_CORE_KEY,
  MDM_ACTIVITY_SEARCH_TYPE_KEY,
  MEASUREMENT_HALF_DAY_VALUE,
  PAGE_IDS,
} from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { UnitTypesKey } from "../../helpers/constants/siteCoreKeys";
import {
  getExpectation,
  getFieldUndefinedForPayloadParams,
  getParsedBEErrors,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  trimValue,
} from "../../helpers/utils";
import useAccordion from "../../hooks/useAccordion";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import {
  selectedUnitSelector,
  topicListSelector,
} from "../../redux/slices/mdmActivity/mdmActivitySelector";
import {
  setActivityFramework,
  setActivityTopicList,
  updateSelectedUnit,
} from "../../redux/slices/mdmActivity/mdmActivitySlice";
import {
  ActivityExpectations,
  ActivityLocale,
  TopicListResponseItem,
} from "../../redux/slices/mdmActivity/models";
import { CancelFieldModal } from "../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { ActivityTopicList } from "./components/ActivityTopicList/ActivityTopicList";
import { AddActivityDetail } from "./components/AddActivity/AddActivityDetail";
import { MdmAddActivityWrapper } from "./MdmAddActivity.style";
import {
  MdmAddActivityFormKeys,
  MdmAddActivityFormTypeValues,
} from "./modelsAndSchemas/models";
import { MdmAddActivitySchema } from "./modelsAndSchemas/ValidationSchema";
import {
  mdmAddActivitySiteCoreKeys,
  mdmAddActivityTopicSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";

export const pageId = PAGE_IDS.MDM_ADD_ACTIVITY;

const initialAccordionStatus = {
  activityDetails: true,
  topics: true,
  all: true,
};

interface MdmAddActivityProps {
  open: boolean;
  onClose: () => void;
  programFrameWorkList: OptionsDataType[];
  projectFrameWorkList: OptionsDataType[];
  handleTopicModal: () => void;
  activitySuccessHandler: () => void;
  isEditActivity?: boolean;
  selectedActivityId?: string;
  handleEditTopic: (id: string) => void;
}

export const MdmAddActivity: FC<MdmAddActivityProps> = ({
  open,
  onClose,
  programFrameWorkList,
  projectFrameWorkList,
  handleTopicModal,
  activitySuccessHandler,
  isEditActivity = false,
  selectedActivityId = "",
  handleEditTopic,
}) => {
  const dispatch = useDispatch();
  const topicList = useSelector(topicListSelector);
  const selectedUnits = useSelector(selectedUnitSelector);
  const { displayToast } = useToast();
  const {
    commonValidationMessages,
    fetchComponentTitle,
    fetchLabel,
    useFetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const [isTopicCreated, setIsTopicCreated] = useState<boolean>(false);
  const [showCancelFieldModal, setShowCancelFieldModal] =
    useState<boolean>(false);
  const [showEditConfirmationModal, setShowEditConfirmationModal] =
    useState<boolean>(false);
  const [footerErrorMessage, setFooterErrorMessage] = useState<string>("");
  const [isUnitsUpdated, setIsUnitsUpdated] = useState<boolean>(false);
  const [searchType, setSearchType] = useState<string>(
    MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
  );

  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const indexRef = useRef<string>("");

  const [showRadioToggleConfirmation, setShowRadioToggleConfirmation] =
    useState(false);
  const searchTypeValueRef = useRef<string>("");

  const programFrameworkRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const projectFrameworkRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const modalityRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );

  const {
    mutateAsync: addActivityApi,
    isPending: isAddActivityPending,
    isError: isAddActivityError,
    isSuccess: isAddActivitySuccess,
  } = useMdmAddActivityMutation();

  const {
    mutateAsync: editActivityApi,
    isPending: isEditActivityPending,
    isError: isEditActivityError,
    isSuccess: isEditActivitySuccess,
    error: editActivityError,
  } = useMdmUpdateActivityMutation();

  const {
    data: activityData,
    isFetching: isActivityDataFetching,
    isError: isActivityDataError,
    refetchWithParams: refetchActivityData,
  } = useMdmGetViewActivityDetailById();
  const {
    mutateAsync: getMdmSearchActivityList,
    isPending: isSearchActivityPending,
    data: searchResult,
    isError: isSearchActivityError,
    isSuccess: isSearchActivitySuccess,
  } = useMdmSearchActivityMutation();

  const addActivityFormDefaultValues = {
    [MdmAddActivityFormKeys.activityName]: "",
    [MdmAddActivityFormKeys.programFramework]: [],
    [MdmAddActivityFormKeys.projectFramework]: [],
    [MdmAddActivityFormKeys.modality]: [],
    [MdmAddActivityFormKeys.units]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: addActivityFormDefaultValues,
    resolver: yupResolver(
      MdmAddActivitySchema(commonValidationMessages, searchType),
    ),
  });

  const {
    getValues,
    handleSubmit,
    control,
    formState: { isDirty, dirtyFields, errors },
    clearErrors,
    setError,
    trigger,
    reset,
  } = methods;

  useEffect(() => {
    if (selectedActivityId && isEditActivity) {
      refetchActivityData({ id: selectedActivityId });
    }
  }, [selectedActivityId]);

  const value = (measurement: string) => {
    return fetchValueByKeyOfDropdown(
      mdmAddActivityTopicSiteCoreKeys.topicListKey,
      mdmAddActivityTopicSiteCoreKeys.minutesList,
      measurement,
    );
  };

  useEffect(() => {
    if (isEditActivityError) {
      dispatch(toggleLoader({ isVisible: false }));
      const error = getParsedBEErrors(editActivityError);
      if (
        isNotEmpty(error) &&
        error[0].errorCode ===
          BE_ERROR_CODES.MDM_ACTIVITY_TOPIC_COMBINATION_IN_USE
      ) {
        setFooterErrorMessage(
          getAddActivityLabel(
            mdmAddActivitySiteCoreKeys.modifyUOMInlineErrorMessage,
          ),
        );
      } else {
        displayToast(
          commonLabels.someThingWentWrongPleaseTryAgainLater,
          "error",
        );
      }
    }
  }, [editActivityError, isEditActivityError]);

  const getTopicList = () => {
    dispatch(updateSelectedUnit(activityData?.unit));
    return activityData?.topics.map((element: TopicListResponseItem) => ({
      isExisting: true,
      unit: element.units,
      defaultValueExceptions: element.expectations.map(
        (expectation: ActivityExpectations) => ({
          units: expectation?.unit,
          country: expectation?.countryTitle,
          measurementName: getExpectation(
            expectation.unit,
            expectation.measurement,
            value,
          ),

          ...expectation,
        }),
      ),
      candidateStatus: element.participantStatus,
      localizedTopics: element.locale.map((locale: ActivityLocale) => ({
        name: locale.localeName,
        ...locale,
      })),
      ...element,
    }));
  };

  useEffect(() => {
    if (isNotEmpty(activityData) && isEditActivity) {
      const searchType = activityData?.type;
      const programFrameWorkIds = activityData?.programFrameWork.map(
        (item: IdNameList) => item.id,
      );
      const modalityIds = activityData?.modality;
      resetFormValues({
        activityName: activityData?.activityName,
        modality: modalityIds,
        units: activityData?.unit,
        searchType,
        ...(isSearchTypeProgram(searchType)
          ? {
              programFramework: programFrameWorkIds,
            }
          : {
              projectFramework: programFrameWorkIds,
            }),
      });
      dispatch(setActivityTopicList(getTopicList()));
      dispatch(setActivityFramework(activityData?.type));
    }
  }, [activityData, isEditActivity, isActivityDataFetching]);

  useEffect(() => {
    if (isActivityDataError || isAddActivityError || isSearchActivityError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isActivityDataError, isAddActivityError, isSearchActivityError]);

  useEffect(() => {
    if (isAddActivitySuccess) {
      displayToast(
        getAddActivityLabel(mdmAddActivitySiteCoreKeys.activityCreatedMessage),
      );
      activitySuccessHandler();
    }
    if (isEditActivitySuccess) {
      displayToast(
        getAddActivityLabel(mdmAddActivitySiteCoreKeys.activityUpdatedMessage),
      );
      activitySuccessHandler();
    }

    dispatch(toggleLoader({ isVisible: false }));
  }, [isAddActivitySuccess, isEditActivitySuccess]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isAddActivityPending ||
          isActivityDataFetching ||
          isEditActivityPending ||
          isSearchActivityPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isAddActivityPending,
    isActivityDataFetching,
    isEditActivityPending,
    isSearchActivityPending,
  ]);
  useEffect(() => {
    if (isSearchActivitySuccess) {
      const isDuplicate = searchResult?.data.length !== 0;
      lastCheckWasDuplicateRef.current = isDuplicate;
      if (isDuplicate) {
        showDuplicateError();
      }
    }
  }, [isSearchActivitySuccess]);

  const lastCheckedNameRef = useRef<string>("");
  const lastCheckedFrameworkTypeRef = useRef<string>(
    MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
  );
  const lastCheckWasDuplicateRef = useRef<boolean | null>(null);
  const isNameUnchanged = (value: string) => {
    const original = trimValue(activityData?.activityName).toLowerCase();
    const current = trimValue(value ?? "").toLowerCase();
    return isEditActivity && original === current;
  };

  const showDuplicateError = () => {
    clearErrors(MdmAddActivityFormKeys.activityName);
    setTimeout(() => {
      setError(MdmAddActivityFormKeys.activityName, {
        type: "custom",
        message: getAddActivityLabel(
          mdmAddActivitySiteCoreKeys.activityNameAlreadyExistsMessage,
        ),
      });
    }, 0);
  };

  const clearAllFormErrors = () => {
    if (isNotEmpty(errors)) {
      clearErrors();
    }
  };

  const resetFormValues = (
    data: MdmAddActivityFormTypeValues,
    shouldKeepErrors: boolean = false,
  ) => {
    reset(
      {
        [MdmAddActivityFormKeys.activityName]: getValueOrBlank(
          data?.activityName,
        ),
        [MdmAddActivityFormKeys.programFramework]: data?.programFramework ?? [],
        [MdmAddActivityFormKeys.projectFramework]: data?.projectFramework ?? [],
        [MdmAddActivityFormKeys.modality]: data?.modality ?? [],
        [MdmAddActivityFormKeys.units]: getValueOrBlank(data?.units),
      },
      {
        keepErrors: shouldKeepErrors,
      },
    );
    setSearchType(data.searchType);
    setTimeout(() => {
      programFrameworkRef.current?.resetValues(data?.programFramework ?? []);
      projectFrameworkRef.current?.resetValues(data?.projectFramework ?? []);
      modalityRef.current?.resetValues(data?.modality ?? []);
    }, 200);
  };

  const getValidFormValues = () => {
    const {
      activityName,
      programFramework,
      modality,
      units,
      projectFramework,
    } = getValues();

    const fields = getFieldUndefinedForPayloadParams({
      activityName,
      programFramework,
      projectFramework,
      modality,
      units,
    });
    return fields;
  };

  const handleRadioToggle = (searchTypeValue: string) => {
    searchTypeValueRef.current = searchTypeValue;
    const fields = getValidFormValues();
    if (isNotEmpty(fields)) {
      setShowRadioToggleConfirmation(true);
      return;
    }
    clearAllFormErrors();
    setSearchType(searchTypeValue);
  };

  const handleSearchTypeToggleConfirmation = () => {
    clearAllFormErrors();
    resetFormValues({
      searchType: searchTypeValueRef.current,
    });
    if (!isEditActivity) {
      dispatch(setActivityTopicList([]));
    }
    setShowRadioToggleConfirmation(false);
  };

  const handleSearchTypeToggleCancel = () => {
    const searchTypePreviousValue =
      searchTypeValueRef.current === MDM_ACTIVITY_SEARCH_TYPE_KEY.program
        ? MDM_ACTIVITY_SEARCH_TYPE_KEY.project
        : MDM_ACTIVITY_SEARCH_TYPE_KEY.program;
    const {
      activityName,
      programFramework,
      modality,
      units,
      projectFramework,
    } = getValues();

    resetFormValues(
      {
        activityName,
        programFramework,
        modality,
        units,
        projectFramework,
        searchType: searchTypePreviousValue,
      },
      isNotEmpty(errors),
    );

    setShowRadioToggleConfirmation(false);
  };

  const handleActivityNameOnBlur = async (value: string) => {
    const trimmedValue = trimValue(value ?? "");

    if (isNameUnchanged(trimmedValue)) return;

    if (
      lastCheckedNameRef.current === trimmedValue &&
      lastCheckedFrameworkTypeRef.current === searchType
    ) {
      lastCheckWasDuplicateRef.current
        ? showDuplicateError()
        : clearErrors(MdmAddActivityFormKeys.activityName);
      return;
    }

    const isValid = await trigger(MdmAddActivityFormKeys.activityName);
    if (!isValid) return;

    lastCheckedNameRef.current = trimmedValue;
    lastCheckedFrameworkTypeRef.current = searchType;

    const params: SearchMdmActivityParams = {
      activityTypeFilter: EQUAL_TO_FILTER_SITE_CORE_KEY,
      activityType: trimmedValue,
      topicFilter: "",
      topic: "",
      activityStatus: "",
      programFrameworkId: "",
      type: searchType,
    };
    getMdmSearchActivityList(params);
  };

  const getAddActivityLabel = useFetchLabel(
    mdmAddActivitySiteCoreKeys.activityDetailsKey,
  );

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const handleModalClose = () => {
    const fields = getValidFormValues();
    if (isEmpty(fields) && !isTopicCreated) return onClose();
    setShowCancelFieldModal(true);
  };

  useEffect(() => {
    if (topicList.length > 0) {
      const isNewTopicCreate =
        isEditActivity &&
        topicList.some((topic) => topic.editFlowChanged === true && !topic.isExisting);
      setIsTopicCreated(isNewTopicCreate);
      setFooterErrorMessage("");
    }
  }, [topicList]);

  const unitWatch = useWatch({
    name: MdmAddActivityFormKeys.units,
    control,
  });

  const areAllTopicUnitsSame = (): boolean => {
    if (!topicList.length) return true;
    return topicList.every((topic) => topic.unit === selectedUnits);
  };

  useEffect(() => {
    const { units } = getValues();
    if (dirtyFields.units && topicList.length > 0) {
      setIsUnitsUpdated(true);
      if (areAllTopicUnitsSame()) {
        setFooterErrorMessage("");
        setIsUnitsUpdated(false);
      }
    }
    if (units === UnitTypesKey.HalfDay) {
      const updatedTopicList = topicList.map((topic) => ({
        ...topic,
        unit: UnitTypesKey.HalfDay,
        units: UnitTypesKey.HalfDay,
        measurement: MEASUREMENT_HALF_DAY_VALUE,
      }));
      dispatch(setActivityTopicList(updatedTopicList));
    }
  }, [unitWatch]);

  const isSearchTypeProgram = (searchTypeValue: string) =>
    searchTypeValue === MDM_ACTIVITY_SEARCH_TYPE_KEY.program;

  const apiData = () => {
    const {
      activityName,
      programFramework,
      modality,
      units,
      projectFramework,
    } = getValues();

    return {
      activityType: activityName,
      programFrameWorkIds: isSearchTypeProgram(searchType)
        ? programFramework
        : projectFramework,
      modality: modality,
      units,
      topicLst: topicList
        .filter((topic) => {
          return !isEditActivity || (isEditActivity && !topic.isExisting);
        })
        .map((topic) => ({
          topic: topic.topic || topic.topicType,
          measurement: topic.measurement,
          deliverableIds: topic.deliverables.map(
            (deliverable) => deliverable.id,
          ),
          activityStatus: topic.activityStatus,
          candidateStatus: topic.candidateStatus,
          triggerBilling: topic.triggerBilling,
          countsTowardsLastContactDate: topic.countsTowardsLastContactDate,
          triggerTouchPointSurvey: topic.triggerTouchPointSurvey,
          defaultValueExceptions: topic.defaultValueExceptions.map(
            (exception) => ({
              countryCode: exception.countryCode,
              units: exception.units,
              measurement: exception.measurement,
            }),
          ),
          localizedTopics: topic.localizedTopics.map((localizedTopic) => ({
            name: localizedTopic.name,
            localeKey: localizedTopic.localeKey,
          })),
        })),
    };
  };

  const handleSaveActivityApi = () => {
    setShowEditConfirmationModal(false);
    if (isEditActivity) {
      editActivityApi({
        id: selectedActivityId,
        ...apiData(),
      });
    } else {
      addActivityApi(apiData());
    }
  };

  const conditions = [
    {
      condition: topicList.length === 0,
      action: () =>
        setFooterErrorMessage(
          fetchLabel(
            mdmAddActivitySiteCoreKeys.topicListKey,
            mdmAddActivitySiteCoreKeys.addAtLeastOneTopicMessage,
          ),
        ),
    },
    {
      condition: !areAllTopicUnitsSame(),
      action: () =>
        setFooterErrorMessage(
          getAddActivityLabel(
            mdmAddActivitySiteCoreKeys.updateUnitValuesMessage,
          ),
        ),
    },
    {
      condition: (isDirty || isTopicCreated) && isEditActivity,
      action: () => setShowEditConfirmationModal(true),
    },
  ];
  const handleValidSubmit = async () => {
    const isValid = await trigger();
    const currentName = getValues(MdmAddActivityFormKeys.activityName)?.trim();
    const nameIsUnchanged = isNameUnchanged(currentName);
    if (isEmpty(currentName) && !isValid) {
      return;
    }
    const isDuplicate = searchResult?.data.length !== 0;

    if (isDuplicate && !nameIsUnchanged) {
      showDuplicateError();
    }

    if (isValid && (!isDuplicate || nameIsUnchanged)) {
      handleActivitySubmit();
    }
  };

  const handleActivitySubmit = () => {
    const matched = conditions.find(({ condition }) => condition);
    if (matched) {
      matched.action();
    } else {
      handleSaveActivityApi();
    }
  };

  const openAddTopicHandler = () => {
    handleSubmit(() => handleTopicModal())();
  };

  const renderTopicHeader = () => {
    return (
      <MuiBox className="topic-header">
        <MuiButtonTertiary
          data-testid={`${pageId}-add-topic`}
          onClick={openAddTopicHandler} //will open topic modal
        >
          <AddCircleOutlineRounded />
          {fetchLabel(
            mdmAddActivitySiteCoreKeys.topicListKey,
            mdmAddActivitySiteCoreKeys.addTopicLink,
          )}
        </MuiButtonTertiary>
      </MuiBox>
    );
  };

  const handleDeleteTopic = (id: string) => {
    indexRef.current = id;
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteClick = () => {
    // TO-DO:
  };

  const handleDeleteModalClose = () => {
    setShowDeleteConfirmModal(false);
  };

  const buttonTitle = useMemo(() => {
    const buttonData = {
      primaryButtonTitle: getAddActivityLabel(
        isEditActivity
          ? mdmAddActivitySiteCoreKeys.updateActivityButton
          : mdmAddActivitySiteCoreKeys.saveActivity,
      ),
      disablePrimaryButton: isEditActivity,
      secondaryButtonTitle: commonLabels.close,
    };
    if ((isEditActivity && (isDirty || isTopicCreated)) || !isEditActivity) {
      buttonData.secondaryButtonTitle = commonLabels.cancel;
      buttonData.disablePrimaryButton = false;
    }
    return buttonData;
  }, [isEditActivity, isDirty, isTopicCreated]);

  const modalTitle = useMemo(() => {
    if (isEditActivity) {
      return replaceHashWithValue(
        getAddActivityLabel(mdmAddActivitySiteCoreKeys.editActivityHeader),
        getValueOrBlank(activityData?.activityName),
      );
    }

    return getAddActivityLabel(
      mdmAddActivitySiteCoreKeys.createNewActivityHeader,
    );
  }, [activityData]);

  return (
    <MuiModal
      data-testid={COMPONENT_IDS.COMMON_MODAL_TITLE}
      onClose={handleModalClose}
      title={modalTitle}
      open={open}
      primaryButtonTitle={buttonTitle.primaryButtonTitle}
      onPrimaryButtonClick={handleValidSubmit}
      disablePrimaryButton={buttonTitle.disablePrimaryButton}
      onSecondaryButtonClick={handleModalClose}
      secondaryButtonTitle={buttonTitle.secondaryButtonTitle}
      hasLargeContent
      footerErrorMessage={footerErrorMessage}
      className="mdm-add-activity-modal"
    >
      <MdmAddActivityWrapper>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(mdmAddActivitySiteCoreKeys.activityDetailsKey)}
          onChange={() => handleIndividualAccordionChange("activityDetails")}
          isExpanded={accordionStatus.activityDetails}
        >
          {isUnitsUpdated && (
            <InformationBadge
              data-testid="information-badge"
              icon={<WarningIcon className="warning-icon" />}
              position="start"
              message={getAddActivityLabel(
                mdmAddActivitySiteCoreKeys.modifiedUnitAlertMessage,
              )}
            />
          )}
          <FormProvider {...methods}>
            <AddActivityDetail
              pageId={pageId}
              programFrameworkRef={programFrameworkRef}
              projectFrameworkRef={projectFrameworkRef}
              modalityRef={modalityRef}
              programFrameWorkList={programFrameWorkList}
              projectFrameWorkList={projectFrameWorkList}
              handleActivityNameOnBlur={handleActivityNameOnBlur}
              data-testid="topic-input"
              onRadioToggle={handleRadioToggle}
              isEditActivity={isEditActivity}
              searchType={searchType}
            />
          </FormProvider>
        </AccordionHeader>
        <AccordionHeader
          title={renderHeader(mdmAddActivitySiteCoreKeys.topicListKey)}
          onChange={() => handleIndividualAccordionChange("topics")}
          isExpanded={accordionStatus.topics}
        >
          {renderTopicHeader()}
          <ActivityTopicList
            rows={topicList}
            pageId={pageId}
            handleDeleteTopic={handleDeleteTopic}
            handleEditTopic={handleEditTopic}
          />
        </AccordionHeader>
        {showCancelFieldModal && (
          <CancelFieldModal
            data-testid="cancel-field-modal"
            showModal={showCancelFieldModal}
            handleModalClose={() => setShowCancelFieldModal(false)}
            handlePrimaryButtonClick={onClose}
          />
        )}

        {showDeleteConfirmModal && (
          <WarningConfirmationModal
            data-testid="delete-confirm-modal"
            show={showDeleteConfirmModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleDeleteModalClose}
            onPrimaryButtonClick={handleDeleteClick}
            labelContent={fetchLabel(
              mdmAddActivitySiteCoreKeys.topicListKey,
              mdmAddActivitySiteCoreKeys.deleteTopicConfirmationMessage,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
        {showEditConfirmationModal && (
          <WarningConfirmationModal
            show={showEditConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={() => setShowEditConfirmationModal(false)}
            onPrimaryButtonClick={handleSaveActivityApi}
            labelContent={getAddActivityLabel(
              mdmAddActivitySiteCoreKeys.updateActivityChangesMessage,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}

        {showRadioToggleConfirmation && (
          <WarningConfirmationModal
            show={showRadioToggleConfirmation}
            modalTitle={commonLabels.confirmation}
            onClose={handleSearchTypeToggleCancel}
            onPrimaryButtonClick={handleSearchTypeToggleConfirmation}
            onSecondaryButtonClick={handleSearchTypeToggleCancel}
            labelContent={getAddActivityLabel(
              mdmAddActivitySiteCoreKeys.areYouSureYouWantToChangeTheFrameworkType,
            )}
            primaryButtonTitle={commonLabels.yes}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </MdmAddActivityWrapper>
    </MuiModal>
  );
};
